import { Formik } from 'formik';
import React, { Dispatch, SetStateAction, useState } from 'react';
import { Form } from 'react-bootstrap';
import * as yup from 'yup';

import { TextField } from '@components/FormFields';
import { useCurrentUser, useCurrentUserUpdate } from '@hooks';
import { community_comments_path, new_session_path } from '@routes';
import request from '@utils/backend-api/request';
import type { FormikSubmitForm } from '@utils/types';

import { CommentData, ModalContent } from '.';
import Benefits from '../Benefits';
import { nameSchema } from '../pages/Settings/SettingsPage/Account/schema';
import StepContainer from './StepContainer';

const BENEFITS = [
  'Comment on articles',
  'Participate in the subscriber forum',
  'Use the subscriber directory',
  'Message with subscribers',
];

type StepEnableProfileProps = {
  nextStep: () => void;
  isLoggedIn: boolean;
  isSaving: boolean;
  setIsSaving: Dispatch<SetStateAction<boolean>>;
  setProfileEnabled: Dispatch<SetStateAction<boolean>>;
  isTriggeredByComment: boolean;
  handleSubmitComment?: FormikSubmitForm;
  commentData: CommentData;
  modalContent?: ModalContent;
  getCurrentTriggerComment: () => HTMLButtonElement;
};

const StepEnableProfile = ({
  nextStep,
  isLoggedIn,
  isSaving,
  setIsSaving,
  setProfileEnabled,
  isTriggeredByComment,
  handleSubmitComment,
  commentData,
  modalContent,
  getCurrentTriggerComment,
}: StepEnableProfileProps) => {
  const { currentUser, isLoadingCurrentUser } = useCurrentUser();
  const { updateCurrentUserAsync } = useCurrentUserUpdate();
  const [error, setError] = useState();

  const handleSubmit = async (values?: { name?: string }) => {
    setIsSaving(true);

    try {
      await updateCurrentUserAsync({
        name: values?.name || null,
        profile_enabled: true,
        message_enabled: true,
        // TODO: should this set commenting_enabled as well, to match user.permitted_to_comment?
      });

      // If we are in a webview on the mobile app, send a message indicating that the mobile app
      // should reload the user data.
      window.ReactNativeWebView?.postMessage(JSON.stringify({ event: 'reloadUserData' }));

      setProfileEnabled(true);
      setIsSaving(false);
      // create comment if enabled profile is triggered by comment
      if (isTriggeredByComment) {
        // used for forum posts in react because we can pass submit method (nice)
        if (handleSubmitComment) {
          handleSubmitComment();
        } else {
          // TODO: remove
          // have to pass values and call method to update comments when rendered from rails
          const triggerComment = getCurrentTriggerComment();
          if (triggerComment) {
            // TODO: Remove when we convert article comments to react
            const input = triggerComment.form.querySelector<HTMLInputElement>('.comment-editor');
            const body = input.value;
            await request('post', community_comments_path(), {
              community_comment: { ...commentData, body },
            });
          }
        }
      }
      nextStep();
    } catch (err) {
      setError(err.message);
      setIsSaving(false);
    }
  };

  const nameFormOrButton =
    !currentUser?.name || currentUser?.name === 'Guest' ? (
      <Formik
        initialValues={{ name: '' }}
        onSubmit={handleSubmit}
        validationSchema={yup.object().shape({ name: nameSchema })}
      >
        {(formik) => (
          <Form noValidate onSubmit={formik.handleSubmit} className="text-left">
            <TextField name="name" label="Full Name" />
            <button disabled={isSaving} type="submit" className="action-button-with-form">
              {modalContent.linkText}
            </button>
          </Form>
        )}
      </Formik>
    ) : (
      <button disabled={isSaving} onClick={() => handleSubmit()}>
        {modalContent.linkText}
      </button>
    );

  return (
    <StepContainer
      heading={modalContent.heading}
      subheading={modalContent.subheading}
      error={error}
    >
      <div>
        <div className="action-container">
          {modalContent.linkUrl ? (
            <a href={`${modalContent.linkUrl}`}>{modalContent.linkText}</a>
          ) : isLoadingCurrentUser ? null : (
            nameFormOrButton
          )}
        </div>
        {!isLoggedIn && (
          <div className="log-in-wrapper">
            Already have an account? <a href={new_session_path()}>Sign in here</a>
          </div>
        )}
        <Benefits benefits={BENEFITS} topMargin="Large" />
      </div>
    </StepContainer>
  );
};
StepEnableProfile.displayName = 'StepEnableProfile';

export default StepEnableProfile;
